/**
 * Přístup projektu — DEV / PROD / MCP, OAuth jen jako přepínač pod MCP.
 * Stejné ovládání pro desktop modal i telefonní nastavení.
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Copy, ExternalLink, KeyRound } from 'lucide-react';
import { api } from '../../../lib/api';
import { AsyncStatusToggle } from '../../../components/ui';

export interface ProjectAccessPanelProps {
  orchestratorProjectId?: string | null;
  previewUrl?: string | null;
  prodUrl?: string | null;
  mcpUrl?: string | null;
  mcpEnabled?: boolean;
  oauthEnabled?: boolean;
  oauthPlatformEnabled?: boolean;
  workspaceService?: string | null;
  productionService?: string | null;
  workspaceInstance?: string | null;
  prodInstance?: string | null;
  services?: Record<string, unknown> | null;
  previewOn?: boolean;
  previewActual?: boolean;
  publishOn?: boolean;
  publishActual?: boolean;
  publishSyncing?: boolean;
  /** Veřejná adresa slotu Náhled na produkčním frontendu. null = vypnuto. */
  slotPreviewUrl?: string | null;
  /** Slug projektu — klíč aplikace (platform-hub.app_key_set) se váže na něj. */
  slug?: string | null;
  /** Deklarace SSO projektu (codable.project_get → sso.mode). */
  ssoMode?: 'off' | 'platform' | 'custom';
  /** Klíč aplikace pro Platform Hub (codable.project_get → hub_app_key_enabled). */
  appKeyEnabled?: boolean;
  queryKeys?: unknown[][];
  onOpenIde?: () => void;
  className?: string;
}

function pickApphostService(
  services: Record<string, { module?: string; instance?: string }> | null | undefined,
  instance: string | null,
): string | null {
  if (!services) return null;
  const entries = Object.entries(services);
  if (instance) {
    const hit = entries.find(([, s]) => s?.module === 'apphost' && s?.instance === instance);
    if (hit) return hit[0];
  }
  return entries.find(([, s]) => s?.module === 'apphost')?.[0] ?? null;
}

/** Inline „Zkopírováno“ — sonner toaster se v ACM neschovává do notifikačního centra. */
export function CopyUrlButton({ url, className }: { url: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      toast.error(t('codable.settingsCopyFail', 'Kopírování selhalo'));
      return;
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2200);
  };
  return (
    <button
      type='button'
      className={`inline-flex items-center gap-1 ${
        copied ? 'text-emerald-400' : 'text-gray-500 hover:text-gray-200'
      } ${className ?? ''}`}
      onClick={copy}
      title={t('ui.copy', 'Kopírovat')}
      aria-label={copied ? t('ui.copied', 'Zkopírováno') : t('ui.copy', 'Kopírovat')}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied && (
        <span className='text-[10px] font-medium'>{t('ui.copied', 'Zkopírováno')}</span>
      )}
    </button>
  );
}

function AccessRow(
  {
    label,
    url,
    empty,
    on,
    actual,
    pending,
    disabled,
    onToggle,
    lockHint,
    extra,
  }: {
    label: string;
    url: string | null;
    empty?: string;
    on: boolean;
    actual?: boolean;
    pending?: boolean;
    disabled?: boolean;
    onToggle: (enabled: boolean) => Promise<unknown>;
    lockHint?: ReactNode;
    extra?: ReactNode;
  },
) {
  const { t } = useTranslation();
  const host = url ? url.replace(/^https?:\/\//, '') : null;
  return (
    <div className='px-3 py-3'>
      <div className='flex items-center gap-3'>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <span className='text-sm font-medium text-white'>{label}</span>
            {url && (
              <>
                <CopyUrlButton url={url} />
                <a
                  href={url}
                  target='_blank'
                  rel='noreferrer'
                  className='text-gray-500 hover:text-blue-400'
                >
                  <ExternalLink size={13} />
                </a>
              </>
            )}
          </div>
          {host
            ? (
              <code className='block mt-0.5 font-mono text-[11px] text-gray-400 truncate' title={url ?? undefined}>
                {host}
              </code>
            )
            : (
              <span className='block mt-0.5 text-[11px] text-gray-600'>
                {empty ?? t('codable.settingsAddrMissing', 'není')}
              </span>
            )}
          {lockHint && <div className='mt-1 text-[11px] text-amber-300/90'>{lockHint}</div>}
        </div>
        <AsyncStatusToggle
          value={on}
          actual={actual ?? on}
          pending={pending}
          disabled={disabled}
          onToggle={onToggle}
          onError={(e) => toast.error(String((e as Error)?.message ?? e))}
          className='shrink-0'
        />
      </div>
      {extra}
    </div>
  );
}

export default function ProjectAccessPanel(props: ProjectAccessPanelProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const {
    orchestratorProjectId,
    previewUrl = null,
    prodUrl = null,
    mcpUrl = null,
    mcpEnabled = false,
    oauthEnabled = false,
    oauthPlatformEnabled = false,
    workspaceService = null,
    productionService = null,
    workspaceInstance = null,
    prodInstance = null,
    services = null,
    previewOn = false,
    previewActual,
    publishOn = false,
    publishActual,
    publishSyncing = false,
    slotPreviewUrl = null,
    slug = null,
    ssoMode = 'off',
    appKeyEnabled = false,
    queryKeys = [['codable-project'], ['acm-connect-tokens'], ['phone-codable-project']],
    onOpenIde,
    className,
  } = props;

  const invalidate = () => {
    for (const key of queryKeys) void qc.invalidateQueries({ queryKey: key });
  };

  const callOrch = async (tool: string, args: Record<string, unknown> = {}) => {
    if (!orchestratorProjectId) throw new Error(t('codable.settingsNeedOrch'));
    const r = await api.mcp<{ ok?: boolean; error?: string }>(tool, args);
    if (r.ok === false) throw new Error(r.error ?? t('codable.errorShort', 'chyba'));
    return r;
  };

  const svcMap = (services ?? null) as
    | Record<string, { module?: string; instance?: string }>
    | null;
  const wsSvc = workspaceService ?? pickApphostService(svcMap, workspaceInstance);
  const prodSvc = productionService ?? pickApphostService(svcMap, prodInstance) ?? wsSvc;
  const canOrch = Boolean(orchestratorProjectId);

  const previewMut = useMutation({
    mutationFn: (enabled: boolean) => {
      if (!wsSvc) throw new Error(t('codable.settingsNeedOrch'));
      return callOrch(`${orchestratorProjectId}.apphost.preview_set`, {
        service: wsSvc,
        enabled,
      });
    },
    onSuccess: invalidate,
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const slotSvc = productionService
    ?? (prodInstance && prodInstance !== workspaceInstance
      ? pickApphostService(svcMap, prodInstance)
      : null);
  const showSlotPreview = Boolean(canOrch && slotSvc && slotSvc !== wsSvc);
  const slotPreviewMut = useMutation({
    mutationFn: (enabled: boolean) => {
      if (!slotSvc) throw new Error(t('codable.settingsNeedOrch'));
      return callOrch(`${orchestratorProjectId}.apphost.preview_set`, {
        service: slotSvc,
        enabled,
      });
    },
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['apphost-release-carousel'] });
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const prodMut = useMutation({
    mutationFn: (enabled: boolean) => {
      if (!prodSvc) throw new Error(t('codable.settingsNeedOrch'));
      return callOrch(
        `${orchestratorProjectId}.apphost.${enabled ? 'publish' : 'unpublish'}`,
        { service: prodSvc },
      );
    },
    onSuccess: invalidate,
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const mcpMut = useMutation({
    mutationFn: (enabled: boolean) =>
      callOrch(
        `${orchestratorProjectId}.orchestrator.${enabled ? 'mcp-publish' : 'mcp-unpublish'}`,
      ),
    onSuccess: invalidate,
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const oauthMut = useMutation({
    mutationFn: (enabled: boolean) =>
      callOrch(
        `${orchestratorProjectId}.orchestrator.${enabled ? 'oauth-publish' : 'oauth-unpublish'}`,
      ),
    onSuccess: invalidate,
    onError: (e) => toast.error(String((e as Error).message)),
  });

  // SSO projektu = deklarace na projektu; wire-sso z ní přepočítá KC klienty,
  // .env.local i CI proměnné ostrého buildu. Přepínač čte to, co zapisuje.
  const ssoMut = useMutation({
    mutationFn: (enabled: boolean) =>
      callOrch(`${orchestratorProjectId}.orchestrator.wire-sso`, {
        mode: enabled ? 'platform' : 'off',
      }),
    onSuccess: invalidate,
    onError: (e) => toast.error(String((e as Error).message)),
  });
  // Klíč aplikace (Platform Hub) — režim bez SSO: ingress vkládá klíč projektu
  // do /acm/, hub ztotožní projekt; práva uživatelů si řeší aplikace sama.
  const appKeyMut = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!slug) throw new Error(t('codable.settingsNeedOrch'));
      const r = await api.mcp<{ ok?: boolean; error?: string }>('platform-hub.app_key_set', {
        project: slug,
        enabled,
      });
      if (r.ok === false) throw new Error(r.error ?? t('codable.errorShort', 'chyba'));
      return r;
    },
    onSuccess: invalidate,
    onError: (e) => toast.error(String((e as Error).message)),
  });

  const oauthLock = !canOrch
    ? t('codable.settingsNeedOrch')
    : !oauthPlatformEnabled
    ? (
      <>
        {t('codable.settingsOauthNeedPlatform')}{' '}
        <Link to='/modules/sso' className='text-plex hover:underline'>
          {t('codable.settingsOauthOpenSso', 'Otevřít SSO')}
        </Link>
      </>
    )
    : !mcpEnabled
    ? t('codable.settingsOauthNeedMcp', 'Nejdřív zapni MCP.')
    : undefined;

  return (
    <div className={className}>
      <div className='rounded-xl border border-gray-800 bg-gray-900/40 divide-y divide-gray-800/60'>
        <AccessRow
          label={t('codable.settingsAddrDev', 'DEV')}
          url={previewUrl || null}
          on={previewOn}
          actual={previewActual ?? previewOn}
          pending={previewMut.isPending}
          disabled={!canOrch || !wsSvc}
          onToggle={(v) => previewMut.mutateAsync(v)}
          lockHint={!canOrch || !wsSvc ? t('codable.settingsNeedOrch') : undefined}
        />
        {showSlotPreview && (
          <AccessRow
            label={t('codable.settingsAddrSlotPreview', 'Náhled')}
            url={slotPreviewUrl || null}
            empty={t('codable.settingsAddrSlotPreviewOff', 'veřejná adresa není zapnutá')}
            on={Boolean(slotPreviewUrl)}
            pending={slotPreviewMut.isPending}
            disabled={!slotSvc}
            onToggle={(v) => slotPreviewMut.mutateAsync(v)}
            extra={
              <p className='mt-1 text-[11px] text-gray-500 leading-snug'>
                {t(
                  'codable.settingsAddrSlotPreviewHint',
                  'Adresa slotu Náhled v Publikaci. Není to DEV workspace. První zapnutí může ostrou adresu krátce restartovat.',
                )}
              </p>
            }
          />
        )}
        <AccessRow
          label={t('codable.settingsAddrProd', 'PROD')}
          url={prodUrl}
          on={publishOn}
          actual={publishActual ?? publishOn}
          pending={prodMut.isPending || publishSyncing}
          disabled={!canOrch || !prodSvc}
          onToggle={(v) => prodMut.mutateAsync(v)}
          lockHint={!canOrch || !prodSvc ? t('codable.settingsNeedOrch') : undefined}
        />
        <AccessRow
          label={t('codable.settingsAddrMcp', 'MCP')}
          url={mcpUrl}
          empty={t('codable.settingsAddrMcpOff', 'vypnuto')}
          on={mcpEnabled}
          pending={mcpMut.isPending}
          disabled={!canOrch}
          onToggle={(v) => mcpMut.mutateAsync(v)}
          lockHint={!canOrch ? t('codable.settingsNeedOrch') : undefined}
          extra={
            <div className='mt-3 ml-0 pl-3 border-l border-gray-800'>
              <div className='flex items-center gap-3'>
                <div className='min-w-0 flex-1'>
                  <div className='text-sm font-medium text-white'>
                    {t('codable.settingsAddrOauth', 'OAuth')}
                  </div>
                  <p className='mt-0.5 text-[11px] text-gray-500 leading-snug'>
                    {t(
                      'codable.settingsOauthHint',
                      'Do konektoru patří jen MCP adresa. Přihlášení si najde sám.',
                    )}
                  </p>
                  {oauthLock && (
                    <div className='mt-1 text-[11px] text-amber-300/90'>{oauthLock}</div>
                  )}
                </div>
                <AsyncStatusToggle
                  value={oauthEnabled}
                  actual={oauthEnabled}
                  pending={oauthMut.isPending}
                  disabled={!canOrch || !mcpEnabled || !oauthPlatformEnabled}
                  onToggle={(v) => oauthMut.mutateAsync(v)}
                  onError={(e) => toast.error(String((e as Error)?.message ?? e))}
                  className='shrink-0'
                />
              </div>
            </div>
          }
        />
        <AccessRow
          label={t('codable.settingsAddrSso', 'Přihlášení ACM (SSO)')}
          url={null}
          empty={ssoMode === 'platform'
            ? t('codable.settingsSsoOn', 'zapnuto — klient v Keycloaku podle domény')
            : ssoMode === 'custom'
            ? t('codable.settingsSsoCustom', 'externí IdP (wire-sso custom)')
            : t('codable.settingsSsoOff', 'vypnuto')}
          on={ssoMode === 'platform'}
          pending={ssoMut.isPending}
          disabled={!canOrch || ssoMode === 'custom'}
          onToggle={(v) => ssoMut.mutateAsync(v)}
          lockHint={!canOrch
            ? t('codable.settingsNeedOrch')
            : ssoMode === 'custom'
            ? t('codable.settingsSsoCustomLock', 'Externí IdP se mění přes wire-sso {mode: custom}.')
            : undefined}
          extra={
            <p className='mt-1 text-[11px] text-gray-500 leading-snug'>
              {t(
                'codable.settingsSsoHint',
                'Po změně URL se KC klient, .env.local i CI proměnné ostrého buildu přepočítají samy.',
              )}
            </p>
          }
        />
        <AccessRow
          label={t('codable.settingsAddrAppKey', 'Klíč aplikace')}
          url={null}
          empty={appKeyEnabled
            ? t('codable.settingsAppKeyOn', 'zapnuto — vkládá ingress do /acm/')
            : t('codable.settingsAppKeyOff', 'vypnuto')}
          on={appKeyEnabled}
          pending={appKeyMut.isPending}
          disabled={!slug}
          onToggle={(v) => appKeyMut.mutateAsync(v)}
          extra={
            <p className='mt-1 text-[11px] text-gray-500 leading-snug'>
              {t(
                'codable.settingsAppKeyHint',
                'Volání /acm/ ztotožní projekt i bez přihlášení uživatele (Platform Hub). Práva uživatelů řeší aplikace sama.',
              )}{' '}
              {t('hubConn.seeConnections', 'Granty, propojení s projekty a provoz: sekce Propojení níže.')}
            </p>
          }
        />
      </div>
      {onOpenIde && (
        <button
          type='button'
          onClick={onOpenIde}
          className='mt-2 flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl border border-gray-800 bg-gray-900/40 hover:border-gray-700 hover:bg-gray-800/40'
        >
          <KeyRound size={15} className='text-gray-400 shrink-0' />
          <div className='min-w-0 flex-1'>
            <div className='text-sm text-white'>{t('codable.settingsConnectIde')}</div>
            <div className='text-[11px] text-gray-500 truncate'>
              {t('codable.settingsConnectIdeHint')}
            </div>
          </div>
        </button>
      )}
    </div>
  );
}
